import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useCMS } from '../../context/CMSContext';
import { toast, Toaster } from 'sonner';
import { Plus, Edit2, Trash2, X, Save, GripVertical } from 'lucide-react';

const AdminFeaturesPage = () => {
  const { getFeatures, createFeature, updateFeature, deleteFeature } = useCMS();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '', description: '', icon: 'Users', category: 'Core', details: [], order: 0
  });
  const [newDetail, setNewDetail] = useState('');

  const categories = ['Core', 'Compliance', 'AI Features', 'Mobile', 'Integration', 'Reporting'];
  const icons = ['Users', 'Shield', 'Brain', 'FileText', 'DollarSign', 'Smartphone', 'Clock', 'CheckCircle', 'BarChart', 'Settings'];

  useEffect(() => { loadFeatures(); }, []);

  const loadFeatures = async () => {
    try {
      const data = await getFeatures(false);
      setFeatures(data);
    } catch (e) {
      toast.error('Failed to load features');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateFeature(editingId, formData);
        toast.success('Feature updated');
      } else {
        await createFeature(formData);
        toast.success('Feature created');
      }
      resetForm();
      loadFeatures();
    } catch (e) {
      toast.error('Failed to save feature');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feature?')) return;
    try {
      await deleteFeature(id);
      toast.success('Feature deleted');
      loadFeatures();
    } catch (e) {
      toast.error('Failed to delete feature');
    }
  };

  const handleEdit = (feature) => {
    setFormData({
      title: feature.title,
      description: feature.description,
      icon: feature.icon,
      category: feature.category,
      details: feature.details || [],
      order: feature.order
    });
    setEditingId(feature.id);
    setShowForm(true);
  };

  const handleToggleActive = async (feature) => {
    try {
      await updateFeature(feature.id, { is_active: !feature.is_active });
      toast.success(`Feature ${feature.is_active ? 'hidden' : 'shown'}`);
      loadFeatures();
    } catch (e) {
      toast.error('Failed to update feature');
    }
  };

  const addDetail = () => {
    if (newDetail.trim()) {
      setFormData(prev => ({ ...prev, details: [...prev.details, newDetail.trim()] }));
      setNewDetail('');
    }
  };

  const removeDetail = (index) => {
    setFormData(prev => ({ ...prev, details: prev.details.filter((_, i) => i !== index) }));
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', icon: 'Users', category: 'Core', details: [], order: 0 });
    setEditingId(null);
    setShowForm(false);
  };

  // Group features by category
  const groupedFeatures = features.reduce((acc, feature) => {
    if (!acc[feature.category]) acc[feature.category] = [];
    acc[feature.category].push(feature);
    return acc;
  }, {});

  if (loading) return <div className="text-text-secondary">Loading...</div>;

  return (
    <div className="space-y-6">
      <Toaster position="top-right" richColors />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Features</h1>
          <p className="text-text-secondary mt-1">Manage platform features displayed on the website</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-tech-blue hover:bg-tech-blue/90">
          <Plus size={18} className="mr-2" />
          Add Feature
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-lg border-border-line my-8">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle>{editingId ? 'Edit' : 'Add'} Feature</CardTitle>
              <button onClick={resetForm} className="text-text-secondary hover:text-text-primary">
                <X size={20} />
              </button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Title *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-2">Description *</label>
                  <textarea
                    required
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Icon</label>
                    <select
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    >
                      {icons.map(icon => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-2">Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-2">Feature Details</label>
                  <div className="flex space-x-2 mb-2">
                    <input
                      type="text"
                      value={newDetail}
                      onChange={(e) => setNewDetail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDetail())}
                      className="flex-1 px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      placeholder="Add detail point..."
                    />
                    <Button type="button" onClick={addDetail} variant="outline">
                      <Plus size={18} />
                    </Button>
                  </div>
                  <ul className="space-y-2">
                    {formData.details.map((detail, index) => (
                      <li key={index} className="flex items-center justify-between p-2 bg-page-bg rounded">
                        <span className="text-sm text-text-primary">{detail}</span>
                        <button type="button" onClick={() => removeDetail(index)} className="text-red-500 hover:text-red-600">
                          <X size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit" className="bg-tech-blue hover:bg-tech-blue/90">
                    <Save size={18} className="mr-2" />
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Features by Category */}
      {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => (
        <div key={category}>
          <h2 className="text-lg font-semibold text-text-primary mb-3">{category}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categoryFeatures.map((feature) => (
              <Card 
                key={feature.id} 
                className={`border-border-line ${!feature.is_active ? 'opacity-50' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-text-secondary">#{feature.order}</span>
                      <span className="text-xs px-2 py-1 rounded bg-tech-blue/10 text-tech-blue">{feature.icon}</span>
                    </div>
                    <button
                      onClick={() => handleToggleActive(feature)}
                      className={`text-xs px-2 py-1 rounded ${feature.is_active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}
                    >
                      {feature.is_active ? 'Active' : 'Hidden'}
                    </button>
                  </div>
                  <h3 className="font-semibold text-text-primary mb-1">{feature.title}</h3>
                  <p className="text-sm text-text-secondary line-clamp-2 mb-3">{feature.description}</p>
                  {feature.details?.length > 0 && (
                    <div className="text-xs text-text-secondary mb-3">
                      {feature.details.length} detail points
                    </div>
                  )}
                  <div className="flex justify-end space-x-2 pt-2 border-t border-border-line">
                    <button onClick={() => handleEdit(feature)} className="p-2 text-text-secondary hover:text-tech-blue">
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => handleDelete(feature.id)} className="p-2 text-text-secondary hover:text-red-500">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {features.length === 0 && (
        <div className="text-center py-12 text-text-secondary">
          No features yet. Click "Add Feature" to create one.
        </div>
      )}
    </div>
  );
};

export default AdminFeaturesPage;
